import { useTurnStore } from "../store"
import { isAnatomy } from "./anatomy"
import { sectionId } from "./AnswerSections"
import { idx } from "./format"
import { planGroups, ReadingPlanList } from "./ReadingPlanBlock"
import { useAnswerSession } from "./session"

interface InThisAnswerProps {
  /** The turn being read; its sections are listed. */
  activeTurn: string | null
  /** The section nearest the top of the viewport (or the one shown, one at a time). */
  activeId: string | null
  onGo: (id: string) => void
  /** The thread's reader: no department link when it already reads for the department. */
  readsFor?: "taxpayer" | "revenue"
}

/**
 * The right rail (Results-v5): where you stand, the reading plan, the
 * follow-up and the thread's other questions. Desktop only; on a
 * phone the plan folds under the glance.
 */
export function InThisAnswer({ activeTurn, activeId, onGo, readsFor }: InThisAnswerProps) {
  const order = useTurnStore((s) => s.order)
  const turns = useTurnStore((s) => s.turns)
  const meta = useAnswerSession((s) => s.meta)
  const asks = order.filter((k) => turns[k] && (meta[k]?.kind ?? "ask") === "ask")
  const key = activeTurn && turns[activeTurn] ? activeTurn : order[order.length - 1]
  const view = key ? turns[key] : undefined
  if (!view || view.status === "running") return null

  const plan = view.plan ?? []
  const anatomy = isAnatomy(plan)
  const others = asks.filter((k) => k !== key)
  const where = view.head ? [{ id: `ans-${key}`, label: "Where you stand" }] : []
  const followup = view.status === "done" ? [{ id: "followup", label: "Ask a follow-up" }] : []
  // Mirrors where Turn draws the department's card: a finished taxpayer-side answer with a 03.
  const hasCourts = plan.some((p) => p.tag.toUpperCase() === "COURTS" && view.sections[p.id])
  const dept = view.status === "done" && anatomy && hasCourts && readsFor !== "revenue"
    ? [{ id: `dept-${key}`, label: "The department's side" }]
    : []

  let body: React.ReactNode
  if (anatomy) {
    body = (
      <>
        {where.length ? <ReadingPlanList groups={[]} also={where} alsoLabel={null} current={activeId} onGo={onGo} /> : null}
        <ReadingPlanList
          groups={planGroups(key, plan, view.sections, view.limits ?? [])}
          also={[...dept, ...followup]}
          current={activeId}
          onGo={onGo}
        />
      </>
    )
  } else {
    const legacy = [
      ...where,
      ...plan.flatMap((p, i) => (view.sections[p.id] ? [{ id: sectionId(key, i), label: `${idx(i)} ${p.heading}` }] : [])),
      ...followup,
    ]
    if (!legacy.length && !others.length) return null
    body = <ReadingPlanList groups={[]} also={legacy} alsoLabel={null} current={activeId} onGo={onGo} />
  }

  return (
    <nav className="inans" aria-label="In this answer">
      <p className="h">In this answer</p>
      {body}
      {others.length ? (
        <>
          <p className="h">Earlier in this thread</p>
          <ul className="inans-q">
            {others.map((k) => (
              <li key={k}>
                <a href={`#turn-${k}`} onClick={(e) => { e.preventDefault(); onGo(`turn-${k}`) }}>{turns[k]?.question || "Question"}</a>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="foot">Your history and workspaces are behind the menu at top left.</p>
    </nav>
  )
}
